// tabs/RecipientsTab.js
import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Message } from "primereact/message";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";

function RecipientsTab({ facilities }) {
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCompanyData, setLoadingCompanyData] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentRecipient, setCurrentRecipient] = useState({
    phoneNumber: "",
    email: "",
  });
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [isCompanyDataLoaded, setIsCompanyDataLoaded] = useState(false);

  useEffect(() => {
    if (selectedFacility) {
      fetchRecipients(selectedFacility._id);
    }
  }, [selectedFacility]);

  const fetchRecipients = async (facilityId) => {
    setLoading(true);
    try {
      const response = await makeRequest2(
        `/api/core/billing/facility/${facilityId}/recipients`,
        "GET",
      );
      if (response.success) {
        const data = response.data;
        if (Array.isArray(data)) {
          setRecipients(data);
        } else if (data && Array.isArray(data.recipients)) {
          setRecipients(data.recipients);
        } else {
          setRecipients([]);
          console.error("Unexpected response format:", data);
          toastify("Unexpected response format from API", "warning");
        }
      } else {
        toastify("Failed to fetch recipients: " + response.error, "error");
      }
    } catch (error) {
      toastify("Error fetching recipients: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyDataForFacility = async (facilityId) => {
    setLoadingCompanyData(true);
    setIsCompanyDataLoaded(false);
    try {
      const response = await makeRequest2(
        `/api/core/billing/company/facility/${facilityId}`,
        "GET",
      );

      if (response.success && response.data) {
        setCompanyData(response.data);
        setIsCompanyDataLoaded(true);

        // Pre-populate the form with company data
        // Note: Phone might not be in the company schema, so we use email as fallback
        setCurrentRecipient({
          phoneNumber: response.data.phone || response.data.email || "",
          email: response.data.email || "",
        });

        toastify(
          "Company data loaded - fields pre-populated from " +
            response.data.name,
          "success",
        );
      } else {
        toastify(
          "Could not load company data. Please enter details manually.",
          "warning",
        );
        setCompanyData(null);
        setIsCompanyDataLoaded(false);
      }
    } catch (error) {
      console.error("Error fetching company data:", error);
      toastify("Error loading company data: " + error.message, "error");
      setCompanyData(null);
      setIsCompanyDataLoaded(false);
    } finally {
      setLoadingCompanyData(false);
    }
  };

  const handleFacilitySelect = (facility) => {
    setSelectedFacility(facility);
  };

  const handleOpenAddDialog = async () => {
    // Reset form
    setCurrentRecipient({ phoneNumber: "", email: "" });
    setCompanyData(null);
    setIsCompanyDataLoaded(false);

    // Open dialog
    setShowAddDialog(true);

    // Fetch company data to pre-populate
    if (selectedFacility) {
      await fetchCompanyDataForFacility(selectedFacility._id);
    }
  };

  const handleAddRecipient = async () => {
    // Validate required fields
    if (!currentRecipient.email || !currentRecipient.phoneNumber) {
      toastify("Both email and phone number are required", "error");
      return;
    }

    try {
      const response = await makeRequest2(
        `/api/core/billing/facility/${selectedFacility._id}/recipients`,
        "POST",
        currentRecipient,
      );
      if (response.success) {
        toastify("Recipient added successfully!", "success");
        fetchRecipients(selectedFacility._id);
        setShowAddDialog(false);
        setCurrentRecipient({ phoneNumber: "", email: "" });
        setCompanyData(null);
        setIsCompanyDataLoaded(false);
      } else {
        toastify("Failed to add recipient: " + response.error, "error");
      }
    } catch (error) {
      toastify("Error adding recipient: " + error.message, "error");
    }
  };

  const handleEditRecipient = async () => {
    try {
      const response = await makeRequest2(
        `/api/core/billing/facility/${selectedFacility._id}/recipients/${currentRecipient._id}`,
        "PUT",
        currentRecipient,
      );
      if (response.success) {
        toastify("Recipient updated successfully!", "success");
        fetchRecipients(selectedFacility._id);
        setShowEditDialog(false);
        setCurrentRecipient({ phoneNumber: "", email: "" });
      } else {
        toastify("Failed to update recipient: " + response.error, "error");
      }
    } catch (error) {
      toastify("Error updating recipient: " + error.message, "error");
    }
  };

  const handleDeleteRecipient = async () => {
    try {
      const response = await makeRequest2(
        `/api/core/billing/facility/${selectedFacility._id}/recipients/${selectedRecipient._id}`,
        "DELETE",
      );
      if (response.success) {
        toastify("Recipient deleted successfully!", "success");
        fetchRecipients(selectedFacility._id);
        setShowDeleteDialog(false);
        setSelectedRecipient(null);
      } else {
        toastify("Failed to delete recipient: " + response.error, "error");
      }
    } catch (error) {
      toastify("Error deleting recipient: " + error.message, "error");
    }
  };

  const openEditDialog = (recipient) => {
    setCurrentRecipient(recipient);
    setShowEditDialog(true);
  };

  const openDeleteDialog = (recipient) => {
    setSelectedRecipient(recipient);
    setShowDeleteDialog(true);
  };

  const actionsTemplate = (rowData) => {
    return (
      <div className="d-flex gap-2">
        <Button
          icon="ti ti-edit"
          className="btn btn-outline-primary btn-sm"
          onClick={() => openEditDialog(rowData)}
          tooltip="Edit Recipient"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="ti ti-trash"
          className="btn btn-outline-danger btn-sm"
          onClick={() => openDeleteDialog(rowData)}
          tooltip="Delete Recipient"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  return (
    <div className="row">
      <div className="col-md-6">
        <h5>Facilities</h5>
        <DataTable
          value={facilities || []}
          selectionMode="single"
          onRowClick={(e) => handleFacilitySelect(e.data)}
          paginator
          rows={10}
          emptyMessage="No facilities found"
          className="mb-3"
        >
          <Column field="name" header="Facility Name" sortable />
          <Column field="location" header="Location" sortable />
        </DataTable>
      </div>
      <div className="col-md-6">
        {selectedFacility && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Recipients for {selectedFacility.name}</h5>
              <Button
                label="Add Recipient"
                icon="ti ti-plus"
                className="btn btn-primary"
                onClick={handleOpenAddDialog}
              />
            </div>
            <DataTable
              value={recipients}
              loading={loading}
              paginator
              rows={10}
              emptyMessage="No recipients found"
            >
              <Column field="phoneNumber" header="Phone Number" sortable />
              <Column field="email" header="Email" sortable />
              <Column
                header="Actions"
                body={actionsTemplate}
                style={{ width: "120px" }}
              />
            </DataTable>
          </>
        )}
        {!selectedFacility && (
          <div className="text-center text-muted py-5">
            <i className="ti ti-arrow-left fs-1"></i>
            <p className="mt-3">Select a facility to view recipients</p>
          </div>
        )}
      </div>

      {/* Add Dialog */}
      <Dialog
        visible={showAddDialog}
        onHide={() => {
          setShowAddDialog(false);
          setCompanyData(null);
          setIsCompanyDataLoaded(false);
          setCurrentRecipient({ phoneNumber: "", email: "" });
        }}
        header="Add Recipient"
        modal
        style={{ width: "450px" }}
        className="p-fluid"
      >
        <div className="p-fluid">
          {loadingCompanyData && (
            <div className="text-center mb-4 py-3">
              <ProgressSpinner style={{ width: "40px", height: "40px" }} />
              <p className="text-muted mt-3 mb-0">Loading company data...</p>
            </div>
          )}

          {isCompanyDataLoaded && companyData && (
            <Message
              severity="info"
              text={`Fields pre-populated with data from: ${companyData.name || "Company"}. You can edit these values before saving.`}
              className="mb-3"
              style={{ fontSize: "0.9rem" }}
            />
          )}

          <div className="field mb-3">
            <label htmlFor="phoneNumber" className="font-weight-bold">
              Phone Number <span className="text-danger">*</span>
            </label>
            <InputText
              id="phoneNumber"
              value={currentRecipient.phoneNumber}
              onChange={(e) =>
                setCurrentRecipient({
                  ...currentRecipient,
                  phoneNumber: e.target.value,
                })
              }
              placeholder="Enter phone number (e.g., +254712345678)"
              disabled={loadingCompanyData}
              className="w-100"
            />
            {companyData && (
              <small className="text-muted d-block mt-1">
                Pre-filled from company data - edit as needed
              </small>
            )}
          </div>

          <div className="field mb-4">
            <label htmlFor="email" className="font-weight-bold">
              Email <span className="text-danger">*</span>
            </label>
            <InputText
              id="email"
              type="email"
              value={currentRecipient.email}
              onChange={(e) =>
                setCurrentRecipient({
                  ...currentRecipient,
                  email: e.target.value,
                })
              }
              placeholder="Enter email address"
              disabled={loadingCompanyData}
              className="w-100"
            />
            {companyData && (
              <small className="text-muted d-block mt-1">
                Pre-filled from company data - edit as needed
              </small>
            )}
          </div>

          <div className="d-flex gap-2 justify-content-end mt-4">
            <Button
              label="Cancel"
              icon="ti ti-x"
              className="p-button-text p-button-secondary"
              onClick={() => {
                setShowAddDialog(false);
                setCompanyData(null);
                setIsCompanyDataLoaded(false);
                setCurrentRecipient({ phoneNumber: "", email: "" });
              }}
            />
            <Button
              label="Add Recipient"
              icon="ti ti-check"
              onClick={handleAddRecipient}
              disabled={
                !currentRecipient.email ||
                !currentRecipient.phoneNumber ||
                loadingCompanyData
              }
              className="p-button-primary"
            />
          </div>
        </div>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        visible={showEditDialog}
        onHide={() => {
          setShowEditDialog(false);
          setCurrentRecipient({ phoneNumber: "", email: "" });
        }}
        header="Edit Recipient"
        modal
        style={{ width: "450px" }}
        className="p-fluid"
      >
        <div className="p-fluid">
          <div className="field mb-3">
            <label htmlFor="editPhoneNumber" className="font-weight-bold">
              Phone Number <span className="text-danger">*</span>
            </label>
            <InputText
              id="editPhoneNumber"
              value={currentRecipient.phoneNumber}
              onChange={(e) =>
                setCurrentRecipient({
                  ...currentRecipient,
                  phoneNumber: e.target.value,
                })
              }
              placeholder="Enter phone number"
              className="w-100"
            />
          </div>
          <div className="field mb-4">
            <label htmlFor="editEmail" className="font-weight-bold">
              Email <span className="text-danger">*</span>
            </label>
            <InputText
              id="editEmail"
              type="email"
              value={currentRecipient.email}
              onChange={(e) =>
                setCurrentRecipient({
                  ...currentRecipient,
                  email: e.target.value,
                })
              }
              placeholder="Enter email address"
              className="w-100"
            />
          </div>
          <div className="d-flex gap-2 justify-content-end mt-4">
            <Button
              label="Cancel"
              icon="ti ti-x"
              className="p-button-text p-button-secondary"
              onClick={() => {
                setShowEditDialog(false);
                setCurrentRecipient({ phoneNumber: "", email: "" });
              }}
            />
            <Button
              label="Update"
              icon="ti ti-check"
              onClick={handleEditRecipient}
              disabled={
                !currentRecipient.email || !currentRecipient.phoneNumber
              }
              className="p-button-primary"
            />
          </div>
        </div>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog
        visible={showDeleteDialog}
        onHide={() => {
          setShowDeleteDialog(false);
          setSelectedRecipient(null);
        }}
        header="Confirm Delete"
        modal
        style={{ width: "400px" }}
      >
        <div className="py-3">
          <p className="mb-3">
            Are you sure you want to delete this recipient?
          </p>
          {selectedRecipient && (
            <div className="bg-light p-3 rounded">
              <div className="mb-2">
                <strong>Email:</strong> {selectedRecipient.email}
              </div>
              <div>
                <strong>Phone:</strong> {selectedRecipient.phoneNumber}
              </div>
            </div>
          )}
        </div>
        <div className="d-flex gap-2 justify-content-end mt-4">
          <Button
            label="Cancel"
            icon="ti ti-x"
            className="p-button-text p-button-secondary"
            onClick={() => {
              setShowDeleteDialog(false);
              setSelectedRecipient(null);
            }}
          />
          <Button
            label="Delete"
            icon="ti ti-trash"
            className="p-button-danger"
            onClick={handleDeleteRecipient}
          />
        </div>
      </Dialog>
    </div>
  );
}

export default RecipientsTab;
